import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { termActions } from "../slices/term";
import { getErrorMessage } from "../../utils/errorUtils";
import { TermRequestDto } from "../../shared/dtos/term/TermRequestDto";
import { TermResponseDto } from "../../shared/dtos/term/TermResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const createTerm =
  (values: TermRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(termActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<TermResponseDto>
      >("/terms", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Term created successfully!");
        dispatch(termActions.createSuccess(data.data));
        dispatch(
          termActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(termActions.setErrorCreate(message));
    }
  };

export const getTerms =
  (params?: {
    academicYearId?: string;
    activeOnly?: boolean;
    branchId?: string;
  }) =>
  async (dispatch: AppDispatch) => {
    dispatch(termActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<TermResponseDto[]>
      >("/terms", {
        headers: { requiresAuth: true },
        params,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(termActions.setItems(data.data));
      }
    } catch (error) {
      dispatch(termActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getTermById = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(termActions.setLoadingFetchOne());
  try {
    const { data } = await axiosInstance.get<ApiResponseDto<TermResponseDto>>(
      `/terms/${id}`,
      { headers: { requiresAuth: true } },
    );

    if (data.status === ApiResponseStatus.SUCCESS && data.data) {
      dispatch(termActions.setItem(data.data));
    }
  } catch (error) {
    dispatch(termActions.setErrorFetchOne(getErrorMessage(error)));
  }
};

export const updateTerm =
  (payload: { id: string; values: TermRequestDto }) =>
  async (dispatch: AppDispatch) => {
    dispatch(termActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<ApiResponseDto<TermResponseDto>>(
        `/terms/${payload.id}`,
        payload.values,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Term updated successfully!");
        dispatch(termActions.updateSuccess(data.data));
        dispatch(
          termActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(termActions.setErrorUpdate(message));
    }
  };

export const deleteTerm = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(termActions.setLoadingDelete());
  try {
    const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
      `/terms/${id}`,
      { headers: { requiresAuth: true } },
    );

    if (data.status === ApiResponseStatus.SUCCESS) {
      toast.success(data.message || "Term deleted successfully!");
      dispatch(termActions.deleteSuccess(id));
      dispatch(
        termActions.setServerStatus({
          operation: "delete",
          status: data.statusCode || 200,
        }),
      );
    }
  } catch (error) {
    const message = getErrorMessage(error);
    toast.error(message);
    dispatch(termActions.setErrorDelete(message));
  }
};

export const resetTermServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(termActions.setServerStatus(null));
};
